import React, { Fragment, useCallback } from 'react';
import PageHeader from '../../layout/shared/page-header';
import WindFarmsCard from './components/WindFarmsCard';
import IssuesCard from './components/IssuesCard';
import CarrionCard from './components/CarrionCard';
import DateField from '../../components/forms/DateField';
import moment from 'moment';
import useFilters from '../../hooks/useFilters';
import useParks from '../../hooks/api-calls/useParks';
import useFetch from '../../hooks/useFetch';
import { DashboardService } from '../../services/dashboard/dashboardService';
import CensusCard from './components/CensusCard';
import PreventiveStopsCard from './components/PreventiveStopsCard';
import SpeciesCard from './components/SpeciesCard';
import CustomSelect from '../../components/forms/CustomSelect';
import DailyNumIndividualsCensusChart from './components/DailyNumIndividualsCensusChart';
import DailyNumIndividualsOFCChart from './components/DailyNumIndividualsOFCChart';
import DailyPreventiveStopsAccumulatedTimeChart from './components/DailyPreventiveStopsAccumulatedTimeChart';
import DailyPreventiveStopsCountChart from './components/DailyPreventiveStopsCountChart';

const breadCrumb = [
    { name: 'Inicio' },
    { name: 'Panel de control' },
];

const Dashboard: React.FC = () => {

    // HOOKS
    const { getParksList } = useParks();
    const { filters , updateFilters } = useFilters({
        between_dates : {
            startDate: moment().startOf('month').format('YYYY-MM-DD'),
            endDate: moment().endOf('month').format('YYYY-MM-DD'),
            type: 'createdAt'
        },
        parks: [],
    });


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL NÚMERO TOTAL DE PARQUES EÓLICOS
     * @EN GETS THE TOTAL NUMBER OF WIND FARMS
     */
    // -------------------------------------------------------------------------------------------------------------
    const getDashboardData = useCallback(async () => {
        try {
            const issueService = new DashboardService();
            const response = await issueService.getDashboard(filters);
            return response.getResponseData();
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [filters]);
    // -------------------------------------------------------------------------------------------------------------
    
    const [data, loading, error, refetch] = useFetch(getDashboardData);

    // FUNCTIONS

    const handleDateChange = (date: [Date | null, Date | null], event: React.SyntheticEvent<any> | undefined) => {
        if (date && date[0] && date[1]) {
            let startMoment, endMoment;
            
            if (typeof date[0] === 'string' && typeof date[1] === 'string') {
                startMoment = moment(date[0], 'DD/MM/YYYY');
                endMoment = moment(date[1], 'DD/MM/YYYY');
            } else {
                startMoment = moment(date[0]);
                endMoment = moment(date[1]);
            }
            
            if (startMoment.isValid() && endMoment.isValid()) {
                updateFilters({
                    between_dates: {
                        type: 'createdAt',
                        startDate: startMoment.format('YYYY-MM-DD'),
                        endDate: endMoment.format('YYYY-MM-DD')
                    }
                });
            }
        }
    }

    const handleParkChange = (parks: any[]) => {
        updateFilters({
            parks: parks
        });
    }

    // RENDER 

    return (
        <Fragment>
            <div className='flex flex-grow flex-col px-4 pb-4 md:px-5 lg:px-6 lg:pb-8 3xl:px-8  3xl:pt-4 4xl:px-10'>     
                <PageHeader title="Panel de control" breadcrumb={breadCrumb} headerFilters={
                    <>
                        <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                             <h2 className="text-sm font-semibold text-gray-800 dark:text-white">Parques : </h2>
                            <CustomSelect
                                isSearchable
                                id={'park'}
                                label=''
                                options={getParksList()}
                                onChange={(e: any) => { 
                                    handleParkChange(e); 
                                }}
                                className='w-full md:w-64 bg-white dark:bg-gray-800'
                                isClearable
                            />
                            <h2 className="text-sm font-semibold text-gray-800 dark:text-white">Rango de fechas : </h2>
                            <DateField
                                selectsRange
                                dateFormat={'dd-MMM-yyyy'}
                                className="w-full"
                                placeholderText="Selecciona un rango de fechas"
                                endDate={moment().endOf('month').toDate()}
                                selected={null}
                                startDate={moment().startOf('month').toDate()}
                                onChange={handleDateChange}
                            />
                        </div>
                    </>
                } />
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-4"> 
                    <IssuesCard data={data?.totalIssues} />
                    <CarrionCard data={data?.totalCarrions} />
                    <CensusCard data={data?.totalCensus} />
                    <PreventiveStopsCard data={data?.totalPreventiveStops} />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mt-6">
                    <DailyNumIndividualsCensusChart data={data?.dailyNumIndividualsCensus} />
                    <DailyNumIndividualsOFCChart data={data?.dailyNumIndividualsOFC} />
                    <DailyPreventiveStopsAccumulatedTimeChart data={data?.dailyPreventiveStopsAccumulatedTime} />
                    <DailyPreventiveStopsCountChart data={data?.dailyPreventiveStopsCount} />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 mt-6">
                    <WindFarmsCard data={data?.totalParks} />
                    <SpeciesCard data={data?.totalSpecies} />
                </div>

            </div>
        </Fragment>
    );
};

export default Dashboard;